// src/pages/qbo/CustomerCheckCard.tsx
//
// One customer, both sides (agent 3.18.11): what QuickBooks holds open for
// the customer (invoices, credit memos, unapplied payments, and whatever
// else moves its balance) next to the Advantage documents still open at the
// conversion cutoff, each tagged converted / in QuickBooks by number / not
// in QuickBooks. Used to understand how the Desktop migration carried the
// older receivables before converting them a second time.

import { useState } from 'react';
import { Search } from 'lucide-react';
import qboApi from '../../services/qboApi';
import type { CustomerCheck } from './qboTypes';
import { money } from './qboTypes';

interface Props {
  onMessage: (type: 'error' | 'success' | 'info', text: string) => void;
}

const stateClass = (s: string) =>
  s === 'converted' ? 'text-green-400' : s.startsWith('in QuickBooks') ? 'text-blue-300' : 'text-amber-300';

export default function CustomerCheckCard({ onMessage }: Props) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState<CustomerCheck | null>(null);

  const run = async () => {
    const n = Number(code);
    if (!n) return;
    setLoading(true);
    try {
      setRes((await qboApi.conversionCustomer(n)) as CustomerCheck);
    } catch (e: unknown) {
      onMessage('error', `Customer check: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 mb-5">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div>
          <div className="text-sm font-semibold text-slate-400 flex items-center gap-2"><Search size={14} /> Customer check</div>
          <div className="text-[11px] text-slate-500">What QuickBooks holds open for one customer, next to its Advantage documents open at the cutoff.</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 w-36 font-mono"
            placeholder="Advantage code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
          />
          <button className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-slate-600 hover:bg-slate-500 disabled:opacity-50" onClick={run} disabled={loading || !Number(code)}>
            {loading ? 'Checking…' : 'Check'}
          </button>
        </div>
      </div>

      {res && (
        <div className="space-y-3">
          <div className="text-xs text-slate-300">
            <span className="font-medium text-white">{res.name}</span> <span className="text-slate-500">#{res.code}</span>
            {res.qbo.id ? <span className="text-slate-500"> · QuickBooks "{res.qbo.name}" (id {res.qbo.id})</span> : <span className="text-amber-300"> · not linked to a QuickBooks customer</span>}
            <span className="text-slate-500"> · Advantage balance now ${money(res.adsBalanceNow)}</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">QuickBooks balance</div><div className="text-sm font-mono text-white">${money(res.qbo.balance)}</div><div className="text-[11px] text-slate-500 font-mono">inv {money(res.qbo.openInvoices)} · cr {money(res.qbo.openCredits)} · unapplied {money(res.qbo.unappliedPayments)}</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Unexplained in QuickBooks</div><div className={`text-sm font-mono ${Math.abs(res.qbo.unexplained) < 0.01 ? 'text-slate-400' : 'text-amber-300'}`}>${money(res.qbo.unexplained)}</div><div className="text-[11px] text-slate-500">balance − open documents</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Advantage open at cutoff</div><div className="text-sm font-mono text-white">${money(res.ads.openAtCutoff)}</div><div className="text-[11px] text-slate-500 font-mono">converted {money(res.ads.converted)} · by number {money(res.ads.existingInQbo)}</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Not in QuickBooks by number</div><div className={`text-sm font-mono ${res.ads.remaining ? 'text-amber-300' : 'text-slate-400'}`}>${money(res.ads.remaining)}</div><div className="text-[11px] text-slate-500">placeholder share, if nothing else holds it</div></div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] uppercase text-slate-500 mb-1">QuickBooks open documents ({res.qbo.docs.length})</div>
              <div className="max-h-72 overflow-y-auto rounded border border-slate-700/60">
                <table className="w-full text-xs border-collapse">
                  <tbody>
                    {res.qbo.docs.map((d, i) => (
                      <tr key={i} className="hover:bg-slate-700/30" title={d.note || ''}>
                        <td className="px-2 py-0.5 border-b border-slate-800/40 text-slate-400 whitespace-nowrap">{d.date}</td>
                        <td className="px-2 py-0.5 border-b border-slate-800/40 text-slate-300">{d.type}</td>
                        <td className="px-2 py-0.5 border-b border-slate-800/40 font-mono text-slate-200">{d.docNumber || <span className="text-slate-600">—</span>}</td>
                        <td className="px-2 py-0.5 border-b border-slate-800/40 text-right font-mono text-slate-500">{money(d.total)}</td>
                        <td className="px-2 py-0.5 border-b border-slate-800/40 text-right font-mono text-slate-200">{money(d.balance)}</td>
                      </tr>
                    ))}
                    {res.qbo.docs.length === 0 && <tr><td className="px-2 py-1 text-slate-500">none</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-slate-500 mb-1">Advantage documents open at {res.cutoff} ({res.ads.docs.length})</div>
              <div className="max-h-72 overflow-y-auto rounded border border-slate-700/60">
                <table className="w-full text-xs border-collapse">
                  <tbody>
                    {res.ads.docs.map((d) => (
                      <tr key={d.invoice} className="hover:bg-slate-700/30">
                        <td className="px-2 py-0.5 border-b border-slate-800/40 text-slate-400 whitespace-nowrap">{d.date}</td>
                        <td className="px-2 py-0.5 border-b border-slate-800/40 font-mono text-slate-200">{d.invoice}</td>
                        <td className="px-2 py-0.5 border-b border-slate-800/40 text-right font-mono text-slate-500">{money(d.total)}</td>
                        <td className="px-2 py-0.5 border-b border-slate-800/40 text-right font-mono text-slate-200">{money(d.open)}</td>
                        <td className={`px-2 py-0.5 border-b border-slate-800/40 ${stateClass(d.state)}`}>{d.state}{d.qboId ? <span className="text-slate-600"> {d.qboId}</span> : null}</td>
                      </tr>
                    ))}
                    {res.ads.docs.length === 0 && <tr><td className="px-2 py-1 text-slate-500">none</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <ul className="text-[11px] text-slate-500 space-y-0.5">{res.notes.map((n, i) => <li key={i}>• {n}</li>)}</ul>
        </div>
      )}
    </div>
  );
}
